import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { Banner, Spinner } from '../components/States';
import { dateTime } from '../lib/format';
import type { OrderDetail, OrderStatus } from '../api/types';
import { trackOrderCancel, trackPedidoEnviado } from '../lib/tracking';
import { ETAPAS, brl, posicaoDaEtapa, textoParaCliente } from '../lib/pedidos';
import { abrirAbaEmBranco } from '../lib/whatsapp';

/** De quanto em quanto tempo a tela relê o pedido enquanto ele anda. */
const RELEITURA_MS = 30_000;

/**
 * O pedido depois de fechado, como o cliente acompanha.
 *
 * O centro da tela é a linha de etapas: é a pergunta que o cliente traz
 * ("já compraram? quando chega a troca?"), e ela responde sem ele precisar
 * chamar o atendimento. Enquanto o pedido não termina, a tela relê sozinha:
 * quando a equipe marca a compra, o cliente vê sem recarregar.
 */
export function OrderPage() {
  const { code = '' } = useParams();
  const [dados, setDados] = useState<OrderDetail | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setDados(await api.pedido(code));
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui carregar o pedido.');
    }
  }, [code]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const emAndamento = dados ? !['concluido', 'cancelado'].includes(dados.order.status) : false;
  useEffect(() => {
    if (!emAndamento) return;
    const t = window.setInterval(() => {
      // Aba escondida não precisa de dado novo; volta a ler quando aparecer.
      if (document.visibilityState === 'visible') void carregar();
    }, RELEITURA_MS);
    return () => window.clearInterval(t);
  }, [emAndamento, carregar]);

  /**
   * Envia um pedido que ficou parado antes do atendimento.
   *
   * O normal é o pedido já sair enviado da sacola. Isto cobre quem fechou e o
   * navegador bloqueou a aba, ou a conexão caiu no meio.
   */
  async function enviar() {
    const aba = abrirAbaEmBranco();
    setOcupado(true);
    setErro(null);
    try {
      const res = await api.enviarPedido(code);
      // Conversão só no primeiro envio e com o servidor confirmando.
      if (dados?.order.status === 'aberto') trackPedidoEnviado(dados.order);
      if (res.whatsapp && aba) aba.location.href = res.whatsapp;
      else aba?.close();
      await carregar();
    } catch (e) {
      aba?.close();
      setErro(e instanceof ApiError ? e.message : 'Não consegui enviar o pedido.');
    } finally {
      setOcupado(false);
    }
  }

  async function cancelar() {
    if (!window.confirm('Cancelar este pedido? O atendimento para a compra.')) return;
    setOcupado(true);
    try {
      await api.cancelarPedido(code);
      if (dados) trackOrderCancel(dados.order.kind);
      await carregar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui cancelar.');
    } finally {
      setOcupado(false);
    }
  }

  if (erro && !dados) return <Banner kind="error">{erro}</Banner>;
  if (!dados) return <Spinner label="Carregando o pedido…" />;

  const { order } = dados;
  const saida = order.items.filter((i) => i.side === 'saida');
  const entrada = order.items.filter((i) => i.side === 'entrada');
  const cancelado = order.status === 'cancelado';
  const posicao = posicaoDaEtapa(order.status);
  const atual = textoParaCliente(order.kind, order.status);

  /** Quando o pedido entrou na etapa: a última vez que o status foi esse. */
  const dataDa = (status: OrderStatus) =>
    [...dados.etapas].reverse().find((e) => e.status === status)?.createdAt ?? null;

  return (
    <div className="panel pedido-cliente">
      <header className="pedido-topo">
        <div>
          <span className="pedido-codigo">Pedido {order.code}</span>
          <h2>{atual.titulo}</h2>
          {atual.detalhe && <p className="muted">{atual.detalhe}</p>}
        </div>
        <span className="muted pedido-data">Aberto em {dateTime(order.createdAt)}</span>
      </header>

      {erro && <Banner kind="error">{erro}</Banner>}

      {cancelado ? (
        <Banner kind="error">
          Este pedido foi cancelado{order.cancelReason ? `: ${order.cancelReason}` : '.'}
        </Banner>
      ) : (
        /* A linha de etapas. Cada uma sabe se já passou, se é a atual ou se
           ainda vem; a data aparece quando a etapa aconteceu. */
        <ol className="etapas" aria-label="Andamento do pedido">
          {ETAPAS.map((status, i) => {
            const n = i + 1;
            const estado = n < posicao ? 'feita' : n === posicao ? 'atual' : 'futura';
            const texto = textoParaCliente(order.kind, status);
            const data = estado === 'futura' ? null : dataDa(status);
            return (
              <li key={status} className={`etapa ${estado}`} aria-current={estado === 'atual' ? 'step' : undefined}>
                <span className="etapa-marca" aria-hidden="true">
                  {estado === 'feita' || (estado === 'atual' && status === 'concluido') ? '✓' : n}
                </span>
                <div className="etapa-texto">
                  <b>{texto.titulo}</b>
                  <span>{texto.detalhe}</span>
                  {data && <time dateTime={data}>{dateTime(data)}</time>}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {order.status === 'aberto' && (
        <Banner kind="warn">
          Seu pedido foi criado, mas <b>ainda não chegou ao atendimento</b>. Envie para a compra começar.
        </Banner>
      )}
      {!dados.whatsappConfigurado && !cancelado && order.status !== 'concluido' && (
        <Banner kind="warn">
          O WhatsApp do atendimento não está disponível agora. Copie o resumo e envie pelo suporte do site.
        </Banner>
      )}

      <div className="actions-row">
        {order.status === 'aberto' && (
          <button className="btn primary" onClick={() => void enviar()} disabled={ocupado}>
            {ocupado ? 'Enviando…' : 'Enviar ao atendimento no WhatsApp'}
          </button>
        )}
        {/* Depois de enviado, abrir a conversa não muda nada no pedido: é só o
            link, sem chamar a API de novo. */}
        {(order.status === 'enviado' || order.status === 'processando') && dados.whatsapp && (
          <a className="btn primary" href={dados.whatsapp} target="_blank" rel="noreferrer noopener">
            Falar com o atendimento
          </a>
        )}
        <button className="btn ghost" onClick={() => void navigator.clipboard.writeText(dados.mensagem)}>
          Copiar resumo
        </button>
        {(order.status === 'aberto' || order.status === 'enviado') && (
          <button className="btn ghost" onClick={() => void cancelar()} disabled={ocupado}>
            Cancelar pedido
          </button>
        )}
        <Link className="btn ghost" to="/pedidos">
          Meus pedidos
        </Link>
      </div>

      {saida.length > 0 && (
        <section className="pedido-bloco">
          <h3>{order.kind === 'venda' ? 'Skins' : 'Skins que você recebe'}</h3>
          <ul className="pedido-itens">
            {saida.map((i, n) => (
              <li key={`s${n}`}>
                {i.iconUrl ? <img src={i.iconUrl} alt="" loading="lazy" /> : <span className="sem-img" />}
                <div>
                  <b>{i.name}</b>
                  {i.exterior && <span className="muted">{i.exterior}</span>}
                </div>
                <span className="pedido-item-qtd">{i.qty > 1 ? `${i.qty}×` : ''}</span>
                <b className="pedido-item-valor">{brl(i.totalBrl)}</b>
              </li>
            ))}
          </ul>
        </section>
      )}

      {entrada.length > 0 && (
        <section className="pedido-bloco">
          <h3>Skins que você entrega</h3>
          <ul className="pedido-itens">
            {entrada.map((i, n) => (
              <li key={`e${n}`}>
                {i.iconUrl ? <img src={i.iconUrl} alt="" loading="lazy" /> : <span className="sem-img" />}
                <div>
                  <b>{i.name}</b>
                  {i.exterior && <span className="muted">{i.exterior}</span>}
                </div>
                <span className="pedido-item-qtd" />
                <b className="pedido-item-valor">{brl(i.totalBrl)}</b>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="total-box">
        {order.kind === 'venda' ? (
          <>
            <span>Você recebe</span>
            <b>{brl(order.creditBrl)}</b>
          </>
        ) : order.kind === 'upgrade' ? (
          <>
            <span>Você completa</span>
            <b>{brl(order.toPayBrl)}</b>
            <small>
              crédito de {brl(order.creditBrl)} nas suas skins · total escolhido {brl(order.totalBrl)}
            </small>
          </>
        ) : (
          <>
            <span>Total do pedido</span>
            <b>{brl(order.totalBrl)}</b>
          </>
        )}
      </div>

      <div className="pedido-entrega">
        <b>Entrega no seu trade link</b>
        <span className="muted break">{order.tradeUrl}</span>
      </div>
    </div>
  );
}
